import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useGroups } from '@/app/redux/hooks/groups';
import { useStudents } from '@/app/redux/hooks/students';
import {
  selectInsightsState,
  setSelectedGroupIds,
  setSelectedStudentIds,
  setSelectedTab,
} from '@/app/redux/slices/insightsSlice';
import { setStudentCreationModalOpen } from '@/app/redux/slices/modalsSlice';
import { Student } from '@/common/types/student';
import {
  DashboardViewContainer,
  DashboardViewContent,
  DashboardViewHeader,
  DashboardViewTitle,
} from '@/common/ui/DashboardViewStyles';
import { useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import InsightsFeatureGate from '@/dashboard/Insights/InsightsFeatureGate';
import LearnerLevelDistribution from '@/dashboard/Insights/LearnerLevelDistribution';
import { GroupMultiSelect, LearnerMultiSelect } from '@/dashboard/MultiSelect';
import { SquaresFourIcon, StudentIcon } from '@phosphor-icons/react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@shadcn/components/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@shadcn/components/tabs';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

export default function InsightsView() {
  const dispatch = useAppDispatch();
  const isFeatureEnabled = useIsFeatureEnabled();

  const { students } = useStudents();

  const { groups } = useGroups();
  const { selectedStudentIds, selectedGroupIds, selectedTab } =
    useAppSelector(selectInsightsState);

  // const [isAttemptsLoading, setAttemptsLoading] = useState(false);
  // const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [chartStudents, setChartStudents] = useState<Student[]>([]);

  // const visibleAttempts = useMemo(() => {
  //   return attempts.filter((a) =>
  //     selectedStudentIds.some((id) => id === a.studentId),
  //   );
  // }, [attempts, selectedStudentIds]);

  // useEffect(() => {
  //   (async () => {
  //     try {
  //       setAttemptsLoading(true);
  //       const attempts = await getAttemptsForStudents(selectedStudentIds);
  //       setAttempts(attempts);
  //     } catch (error) {
  //       console.error(error);
  //     } finally {
  //       setAttemptsLoading(false);
  //     }
  //   })();
  // }, []);

  useEffect(() => {
    if (selectedTab === 'learners') {
      const selectedStudents = students.filter((s) =>
        selectedStudentIds.includes(s.id),
      );
      setChartStudents(selectedStudents);
    } else if (selectedTab === 'groups') {
      const selectedGroups = groups.filter((g) =>
        selectedGroupIds.includes(g.id!),
      );
      setChartStudents(
        selectedGroups.flatMap((g) =>
          g.studentIds.map((id) => students.find((s) => s.id === id)!),
        ),
      );
    }
  }, [selectedTab, students, groups, selectedGroupIds, selectedStudentIds]);

  useEffect(() => {
    setChartStudents(students.filter((s) => selectedStudentIds.includes(s.id)));
  }, [selectedStudentIds, students]);

  useEffect(() => {
    if (selectedGroupIds.length === 0) return;
    const selectedGroups = groups.filter((g) =>
      selectedGroupIds.includes(g.id!),
    );
    const selectedStudents = students.filter((s) =>
      selectedGroups.flatMap((g) => g.studentIds).includes(s.id),
    );
    setChartStudents(selectedStudents);
  }, [selectedGroupIds, groups, students]);

  function handleFilterStudents(searchTerm: string) {
    const filtered = (students || []).filter((student) => {
      const key = `${student.studentFirstName} ${student.studentSurname} ${student.spellcastUsername}`;
      const terms = searchTerm.split(' ');
      return terms.every((term) =>
        key.toLowerCase().includes(term.toLowerCase()),
      );
    });

    if (searchTerm) {
      trackMixpanelEvent('Insights Student Search', {
        searchTerm,
        resultsCount: filtered.length,
      });
    }

    return filtered;
  }

  function handleFilterGroups(searchTerm: string) {
    const filtered = (groups || []).filter((group) => {
      const key = `${group.name}`;
      const terms = searchTerm.split(' ');
      return terms.every((term) =>
        key.toLowerCase().includes(term.toLowerCase()),
      );
    });

    if (searchTerm) {
      trackMixpanelEvent('Insights Group Search', {
        searchTerm,
        resultsCount: filtered.length,
      });
    }

    return filtered;
  }

  function handleSelectStudent(id: string) {
    const updatedStudentIds = new Set([...selectedStudentIds]);
    const wasSelected = updatedStudentIds.has(id);

    if (wasSelected) {
      updatedStudentIds.delete(id);
    } else {
      updatedStudentIds.add(id);
    }

    const newStudentIds = Array.from(updatedStudentIds);
    dispatch(setSelectedStudentIds(newStudentIds));

    trackMixpanelEvent('Insights Student Selected', {
      studentId: id,
      selected: !wasSelected,
      totalSelected: newStudentIds.length,
    });
  }

  function handleSelectGroup(id: string) {
    const updatedGroupIds = new Set([...selectedGroupIds]);
    const wasSelected = updatedGroupIds.has(id);

    if (wasSelected) {
      updatedGroupIds.delete(id);
    } else {
      updatedGroupIds.add(id);
    }

    const newGroupIds = Array.from(updatedGroupIds);
    dispatch(setSelectedGroupIds(newGroupIds));

    trackMixpanelEvent('Insights Group Selected', {
      groupId: id,
      selected: !wasSelected,
      totalSelected: newGroupIds.length,
    });
  }

  function handleSelectAll() {
    if (selectedTab === 'learners') {
      const isSelectingAll = selectedStudentIds.length !== students.length;
      const newIds = isSelectingAll ? students.map((s) => s.id) : [];

      dispatch(setSelectedStudentIds(newIds));

      trackMixpanelEvent('Insights Select All Clicked', {
        tab: 'learners',
        selected: isSelectingAll,
        count: newIds.length,
      });
    } else if (selectedTab === 'groups') {
      if (selectedGroupIds.length === groups.length) {
        dispatch(setSelectedGroupIds([]));
      } else {
        dispatch(setSelectedGroupIds(groups.map((g) => g.id!)));
      }
    }
  }

  if (!isFeatureEnabled('insights')) {
    return <InsightsFeatureGate />;
  }

  return (
    <DashboardViewContainer>
      <DashboardViewHeader>
        <DashboardViewTitle>Insights</DashboardViewTitle>
      </DashboardViewHeader>
      <DashboardViewContent>
        <InsightsContent>
          <div className="flex flex-col gap-4 w-full">
            <LearnerLevelDistribution students={chartStudents} />
            {/* <GraphemeAccuracy
            attempts={visibleAttempts}
            loading={isAttemptsLoading}
          /> */}
          </div>

          <Card className="max-w-sm">
            <CardHeader className="pb-4">
              <CardTitle className="">Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs
                defaultValue={selectedTab}
                onValueChange={(value) => {
                  const newTab = value as 'learners' | 'groups';
                  trackMixpanelEvent('Insights Filter Tab Changed', {
                    previousTab: selectedTab,
                    newTab,
                  });
                  dispatch(setSelectedTab(newTab));
                }}
                className="flex flex-col gap-2"
              >
                <div className="flex flex-col gap-1 text-sm">
                  <span className="font-medium">Select Learners</span>
                  <span className="text-muted-foreground">
                    Select individual learners or a group to view insights for
                    those learners
                  </span>
                </div>

                <TabsList className="gap-2 bg-transparent w-full py-4 justify-start">
                  <TabsTrigger value="learners" className="gap-2 w-full">
                    <StudentIcon size={18} weight="light" />
                    Learners
                  </TabsTrigger>
                  <TabsTrigger value="groups" className="gap-2 w-full">
                    <SquaresFourIcon size={18} weight="light" />
                    Groups
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="learners">
                  <LearnerMultiSelect
                    items={students}
                    selectedIds={selectedStudentIds}
                    onSelect={handleSelectStudent}
                    onSelectAll={handleSelectAll}
                    onFilter={handleFilterStudents}
                    onAddItem={() => dispatch(setStudentCreationModalOpen(true))}
                  />
                </TabsContent>
                <TabsContent value="groups">
                  <GroupMultiSelect
                    items={groups}
                    selectedIds={selectedGroupIds}
                    onSelect={handleSelectGroup}
                    onSelectAll={handleSelectAll}
                    onFilter={handleFilterGroups}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </InsightsContent>
      </DashboardViewContent>
    </DashboardViewContainer>
  );
}

const InsightsContent = styled.div`
  width: 100%;
  padding: 8px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;
